import {
  type CamelCased,
  type Creator,
  type CreatorListItem,
  type CreatorListItemServer,
  CreatorStatusEnum,
  type CreatorsV2Filter,
  DefaultIcon,
  getSocialNetwork,
  type ListUsersIncludeParam,
  type RecruitmentData,
  sanitizeSocialSlug,
  type SocialItem,
  type SocialItemEnum,
  type SocialItemServer,
  type SocialMediaResource,
  type UserResource,
  type UsersIndexParams,
  type UsersIndexStatus,
} from '@metapic-apps/shared'
import { type DeepReadonly, markRaw } from 'vue'

import { normalizeClientListItem } from '@/lib/adapters/clients.adapter'
import { normalizeRevenueTier } from '@/lib/adapters/common.adapter'
import { normalizeImage } from '@/lib/adapters/image.adapter'
import { normalizeUserTags } from '@/lib/adapters/user-tags.adapter'

/*
 * ---------------------------------------------------------------------------
 * Legacy `/users/{id}` mapping — reached only from `creator-detail.adapter.ts`.
 * The `/v2/users` counterparts live below; new work belongs there. Delete this
 * block once the creator detail page moves to `/v2/users/{id}`.
 * ---------------------------------------------------------------------------
 */

const normalizeSocialItem = (social: SocialItemServer): SocialItem => {
  const data = getSocialNetwork(social.type)
  const item = {
    id: social.id,
    title: 'Website',
    link: sanitizeSocialSlug(social.type, social.identifier),
    identifier: social.identifier,
    followers: social.followers,
    icon: DefaultIcon,
    isConnected: social.state === 'connected',
    createdAt: social.created_at,
    updatedAt: social.updated_at,
    isPrimary: social.is_primary,
    profileId: social.profile_id,
    type: social.type,
  }

  if (data) {
    item.title = data.title
    item.icon = data.icon
  }

  return markRaw(item)
}

const normalizeCreatorStatus = (data: CreatorListItemServer): CreatorStatusEnum => {
  if (data.is_suspended) {
    return CreatorStatusEnum.Banned
  }

  if (data.is_verified) {
    return CreatorStatusEnum.Approved
  }

  return CreatorStatusEnum.NotApproved
}

export const normalizeCreatorListItem = (data: CreatorListItemServer): CreatorListItem => {
  return {
    id: data.id,
    username: data.username,
    email: data.email,
    firstName: data.first_name,
    lastName: data.surname,
    client: normalizeClientListItem(data.client),
    revenueTier: normalizeRevenueTier(data.revenue_tier),
    latestPaymentDate: data.latest_payment_date,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    socials: data.social_media.map(normalizeSocialItem),
    myPageSlug: data.my_page_slug ?? null,
    isSuspended: data.is_suspended,
    isVerified: data.is_verified,
    status: normalizeCreatorStatus(data),
    ...(data.user_image && { avatar: normalizeImage(data.user_image) }),
    tags: data.user_tags.length ? normalizeUserTags(data.user_tags) : [],
    recruitmentUtm: data.recruitment_utm,
    ...(data.recruitment_data && {
      recruitmentData: {
        ...(data.recruitment_data.campaign && {
          campaign: {
            id: data.recruitment_data.campaign.id,
            name: data.recruitment_data.campaign.name,
            advertiser: {
              id: data.recruitment_data.campaign.advertiser.id,
              name: data.recruitment_data.campaign.advertiser.name,
            },
          },
        }),
      },
    }),
  }
}

/*
 * ---------------------------------------------------------------------------
 * `GET /v2/users` mapping — the current path, behind `metapicApiClient`.
 * ---------------------------------------------------------------------------
 */

export const CREATORS_LIST_INCLUDES: ListUsersIncludeParam[] = [
  'email',
  'user_tags',
  'social_media',
  'client',
  'client.name',
  'revenue_tier',
  'recruitment',
  'latest_payment',
  'my_page',
]

const CREATORS_LIST_PAGE_SIZE = 20
const CREATORS_LIST_SORT_BY = 'created_at:desc'
// Without a status the API hides suspended creators; the admin search must find every state.
const CREATORS_LIST_ANY_STATUS: UsersIndexStatus = 'any'

/** Deeply readonly because `useApi` hands responses back through `readonly()`. */
export type CreatorUserResource = DeepReadonly<CamelCased<UserResource>>

const normalizeCreatorSocial = (
  social: DeepReadonly<CamelCased<SocialMediaResource>>,
): SocialItem => {
  const type = social.type as SocialItemEnum
  const data = getSocialNetwork(type)

  return markRaw({
    id: social.id,
    title: data?.title ?? 'Website',
    link: sanitizeSocialSlug(type, social.identifier),
    identifier: social.identifier,
    followers: social.followers,
    icon: data?.icon ?? DefaultIcon,
    isConnected: social.state === 'connected',
    createdAt: social.createdAt,
    updatedAt: social.updatedAt ?? '',
    isPrimary: social.isPrimary,
    profileId: social.profileId,
    type,
  })
}

const normalizeCreatorStatusV2 = (user: {
  isSuspended: boolean
  isVerified: boolean
}): CreatorStatusEnum => {
  if (user.isSuspended) {
    return CreatorStatusEnum.Banned
  }

  if (user.isVerified) {
    return CreatorStatusEnum.Approved
  }

  return CreatorStatusEnum.NotApproved
}

export const normalizeCreator = (user: CreatorUserResource): Creator => {
  // The spec types this relation as an opaque map, so the shape is asserted rather than derived.
  const recruitmentData = user.recruitmentData as RecruitmentData | null | undefined

  return {
    id: user.id,
    username: user.username,
    email: user.email ?? '',
    firstName: user.firstName,
    lastName: user.surname,
    createdAt: user.createdAt,
    client: user.client ? { id: user.client.id, name: user.client.name ?? '' } : null,
    revenueTier: user.revenueTier ? { id: user.revenueTier.id, name: user.revenueTier.name } : null,
    latestPaymentDate: user.latestPaymentDate ?? null,
    isVerified: user.isVerified,
    isSuspended: user.isSuspended,
    status: normalizeCreatorStatusV2(user),
    // Not via `normalizeImage`: `ImageResource` has no `content_type`.
    ...(user.image && {
      avatar: { key: user.image.key, baseUrl: user.image.baseUrl, url: user.image.url },
    }),
    tags: (user.userTags ?? []).map((tag) => ({ id: tag.id, name: tag.name, slug: tag.slug })),
    socials: (user.socialMedia ?? []).map(normalizeCreatorSocial),
    myPageSlug: user.myPageSlug ?? null,
    recruitmentUtm: user.recruitmentUtm ?? null,
    ...(recruitmentData?.campaign && {
      recruitmentData: {
        campaign: {
          id: recruitmentData.campaign.id,
          name: recruitmentData.campaign.name,
          advertiser: {
            id: recruitmentData.campaign.advertiser.id,
            name: recruitmentData.campaign.advertiser.name,
          },
        },
      },
    }),
  }
}

const handleCreatorsV2Filter = (filter: CreatorsV2Filter): UsersIndexParams => ({
  ...(filter.query && { query: filter.query }),
  ...(filter.advertiserGroup && { store_group_id: filter.advertiserGroup.id }),
  ...(filter.client && { client_id: filter.client.id }),
  status: filter.status ?? CREATORS_LIST_ANY_STATUS,
  ...(filter.segment && { 'segment_ids[]': [filter.segment.id] }),
  ...(filter.userTags?.length && { 'user_tag_ids[]': filter.userTags.map(({ id }) => id) }),
  sort_by: CREATORS_LIST_SORT_BY,
})

export const buildUsersIndexParams = (filter: CreatorsV2Filter): UsersIndexParams => ({
  ...handleCreatorsV2Filter(filter),
  page: filter.page ?? 1,
  size: filter.size ? Number(filter.size) : CREATORS_LIST_PAGE_SIZE,
  'include[]': CREATORS_LIST_INCLUDES,
})

/** Repeats a key per entry for array params, so `include[]` survives the round trip. */
const toSearchParams = (params: UsersIndexParams): URLSearchParams => {
  const search = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, String(item)))
      return
    }

    search.append(key, String(value))
  })

  return search
}

/** The export takes the same filters and includes as the list, but every row rather than a page. */
export const buildCreatorsExportParams = (filter: CreatorsV2Filter): URLSearchParams => {
  const params = toSearchParams({
    ...handleCreatorsV2Filter(filter),
    'include[]': CREATORS_LIST_INCLUDES,
  })
  params.append('format', filter.format ?? 'xlsx')

  return params
}
